import { ChangeEvent, FC, useEffect, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import {
    ICurrencyDto,
    IValidatedPairsDto,
    TCurrencyWithVolumeRecord,
    TPriceWindowApiDto,
    IOrderCreationDto,
    getPrices,
    getValidPairsAndCurrencies,
} from 'nexchange-service'
import config from '@/config'
import Button, { EButtonStyleVariantClass } from '@/src/components/elements/button/Button'
import sendEmail from '@/services/redirect/sendEmail'
import Image from 'next/image'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import DataParamsReducer, {
    SetCoinDataAction,
    dataParamsInitState,
} from '../SupportedAssets/CoinDetailsPage/reducers/exchangeWidget/setCoinTickersToExchangeWidget'
import ExchangeWidget from '../ExchangeWidget/ExchangeWidget'
import { HeroContainer, HeroHeadingSubSection, Section } from './Hero.styles'

const { EXCHANGE_WIDGET_CONFIGS } = config
const {
    FROM_AVAILABILITY_MODE: coinTypeAvailabilityModeFrom,
    TO_AVAILABILITY_MODE: coinTypeAvailabilityModeTo,
} = EXCHANGE_WIDGET_CONFIGS.INDEX

const i18nNamespaces = ['home', 'partners']

const sharedLayoutStyles = `
    display: flex;
    justify-content: space-around;
    align-items: center;
    @media (max-width: ${props => props.theme.bps.sm}) {
        padding: 1rem;
    }
    @media (min-width: ${props => props.theme.bps.sm}) {
        padding: 0.75rem 10rem 1.5rem ;
        flex-direction: column;
        justify-content: space-around;
    }
`

const sharedInfoContainerStyles = `
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-size: 0.625rem;
    h1, h2, h3 {
        color: white;
    }
    > div, span {
        padding: 0.75rem 0;
    }
    @media (max-width: ${props => props.theme.bps.sm}) {
        align-items: center;
    }
`

const LayoutContainer = styled.div`
    ${sharedLayoutStyles}
    @media (max-width: ${props => props.theme.bps.md}) {
        padding: 0 2rem;
        flex-direction: column;

        .landing-img-swap {
            width: 100%;
            height: auto;
            margin-left: 0;
            margin-bottom: 2rem;
            order: -1; // Images first on mobile
        }
    }

    @media (min-width: ${props => props.theme.bps.md}) {
        flex-direction: row;
        justify-content: center;
        padding: 4rem 2rem;

        .landing-img,
        .landing-img-swap {
            width: 530px;
            height: auto;
        }
    }
`

// Create two variants of the container for alternating layouts
const LayoutContainerImageRight = styled(LayoutContainer)`
    @media (min-width: ${props => props.theme.bps.md}) {
        flex-direction: row-reverse;
    }
`

const LayoutGetUsdtContainer = styled.div`
    ${sharedLayoutStyles}
    position: relative;
    padding: 3rem 4rem 0 4rem;
    background: linear-gradient(135deg, rgba(40, 54, 72, 0.95) 0%, rgba(28, 37, 49, 0.95) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    margin: 2rem auto;
    max-width: 1200px;
    width: 90%;
    overflow: visible;
    min-height: 300px;

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: url('/images/easywallet/v3-old.svg') no-repeat;
        background-size: cover;
        opacity: 0.7;
        z-index: 0;
        border-radius: 24px;
    }

    @media (max-width: ${props => props.theme.bps.md}) {
        padding: 2rem 2rem 0 2rem;
        flex-direction: column;
        text-align: center;

        img {
            width: 80%;
            max-width: 300px;
            margin: 2rem auto -100px;
            transform: translateY(20px);
        }
    }

    @media (min-width: ${props => props.theme.bps.md}) {
        min-height: fit-content;
        display: flex;
        align-items: center;

        img {
            width: 300px;
            height: auto;
            transition: transform 0.5s ease;

            &:hover {
                transform: perspective(1000px) rotateY(-5deg) translateY(40px);
            }
        }
    }
`

const LayoutFirstContainer = styled.div`
    ${sharedLayoutStyles}
    background-image: url('/images/easywallet/v3.svg');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    width: 100%;
    justify-content: center;
    padding: 2rem 0rem;
    div {
        max-width: 100%;
    }

    @media (max-width: ${props => props.theme.bps.md}) {
        flex-direction: column-reverse;
        justify-content: space-around;
        background-position: 100% center;
        .first-landing-img {
            margin-bottom: 20px;
        }
    }
    @media (min-width: ${props => props.theme.bps.md}) {
        flex-direction: row;
        min-height: 90vh;
        justify-content: space-around;
        padding: 0 5rem;
    }
    @media (min-width: ${props => props.theme.bps.lg}) {
        // flex-direction: row;
        // padding: 0 20rem;
        // margin: 0 auto;
    }
`

const InfoFirstContainer = styled.div`
    ${sharedInfoContainerStyles}
    text-align: start;
    h1 {
        font-size: ${props => props.theme.fontSize.lg};

        span.highlight {
            background: linear-gradient(
                104deg,
                rgba(111, 120, 245, 0.05) 0%,
                rgba(111, 120, 245, 0.08) 100%
            );
            border: 1px solid rgba(111, 120, 245, 0.1);
            border-radius: 8px;
            padding: 0.2em 0.4em;
            margin: 0 -0.4em;
            transition: all 0.4s ease;
            display: inline-block;
            transform: rotate(-1deg);

            &:hover {
                transform: rotate(0deg) scale(1.02);
                background: linear-gradient(
                    104deg,
                    rgba(111, 120, 245, 0.08) 0%,
                    rgba(111, 120, 245, 0.12) 100%
                );
                border-color: rgba(111, 120, 245, 0.15);
            }
        }
    }
    h2 {
        font-size: ${props => props.theme.fontSize.sm};
        font-weight: 300;
        margin-top: 1rem;
    }
    .buttons {
        display: flex;
        .btn {
            margin-right: 5px;
            justify-content: center;
        }
    }
    @media (max-width: ${props => props.theme.bps.md}) {
        h1 {
            margin-top: 2rem;
            font-size: 56px;
            line-height: 64px;
        }
        h2 {
            font-size: 20px;
            margin: 1rem 0;
        }
        gap: 1rem;
        width: 90vw;
        max-width: auto;
        text-align: center;
        align-items: center;
        padding-top: 2rem;
    }
    @media (min-width: ${props => props.theme.bps.md}) {
        width: 40vw;
    }
    @media (min-width: ${props => props.theme.bps.lg}) {
        width: 25vw;
        h1 {
            font-size: 56px;
            line-height: 64px;
        }
        h2 {
            font-size: 20px;
            margin: 1rem 3rem 1rem 0;
        }
    }
`

const LayoutTwoContainer = styled.div`
    ${sharedLayoutStyles}
    flex-direction: column;
    padding: 2rem;
    @media (min-width: ${props => props.theme.bps.lg}) {
        p {
            font-size: ${props => props.theme.fontSize.md};
            line-height: 32px;
        }
    }
`

const InfoUsdtContainer = styled.div`
    ${sharedInfoContainerStyles}
    position: relative;
    z-index: 1;
    padding: 0;

    margin-top: -2rem;

    h3 {
        font-size: 2.5rem;
        line-height: 1.2;
        margin-bottom: 1rem;
        background: linear-gradient(90deg, #ffffff 0%, #b7b7b7 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    span {
        display: block;
        margin-bottom: 2rem;
        font-size: 1.25rem;
        color: rgba(255, 255, 255, 0.8);
    }

    @media (max-width: ${props => props.theme.bps.md}) {
        margin: 0;
        text-align: center;

        h3 {
            font-size: 2rem;
        }

        span {
            font-size: 1.1rem;
        }
    }

    @media (min-width: ${props => props.theme.bps.xl}) {
        h3 {
            font-size: 3rem;
        }
    }
`

const InfoContainer = styled.div`
    ${sharedInfoContainerStyles}
    text-align: start;
    h3 {
        font-size: ${props => props.theme.fontSize.md};
        line-height: 2rem;
    }
    @media (max-width: ${props => props.theme.bps.md}) {
        h3 {
            font-size: ${props => props.theme.fontSize.lg};
            line-height: 48px;
        }
        width: 80vw;
    }
    @media (min-width: ${props => props.theme.bps.md}) {
        width: 40vw;
    }
    @media (min-width: ${props => props.theme.bps.lg}) {
        width: 25vw;
        h3 {
            font-size: ${props => props.theme.fontSize.xl};
            line-height: 56px;
        }
    }
`

const Paragraph = styled.div`
    width: 100%;
    font-size: ${props => props.theme.fontSize.xs};
    color: #bdc1c5;
    @media (min-width: ${props => props.theme.bps.lg}) {
        font-size: ${props => props.theme.fontSize.md};
        line-height: 32px;
    }
`

const StyledInput = styled.input`
    color: white;
    font-size: 16px;
    width: 100%;
    max-width: 300px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    height: 54px;
    padding: 0 1.5rem;
    margin-right: 1rem;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:focus {
        outline: none;
        border-color: rgba(111, 120, 245, 0.5);
        background: rgba(255, 255, 255, 0.1);
        box-shadow: 0 0 0 2px rgba(111, 120, 245, 0.2);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }

    @media (max-width: ${props => props.theme.bps.md}) {
        width: 100%;
        max-width: none;
        margin: 0 0 1rem 0;
    }
`

const Features = styled.div`
    ${sharedLayoutStyles}
    @media (max-width: ${props => props.theme.bps.md}) {
        padding: 0 2rem;
        flex-direction: column;
        margin-bottom: 3rem;
    }
    @media (min-width: ${props => props.theme.bps.md}) {
        flex-direction: row;
        padding: 50px 0 0;
    }
    @media (min-width: ${props => props.theme.bps.xl}) {
        padding: 0 20rem;
    }
`

const FeatureIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(111, 120, 245, 0.1);
    border-radius: 12px;
    padding: 12px;
    transition: transform 0.3s ease;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    @media (min-width: ${props => props.theme.bps.xl}) {
        width: 56px;
        height: 56px;
    }
`

const FeatureContainer = styled.div`
    display: flex;
    background: linear-gradient(145deg, rgba(40, 54, 72, 0.9) 0%, rgba(40, 54, 72, 0.7) 100%);
    backdrop-filter: blur(10px);
    color: white;
    flex-direction: column;
    font-size: ${props => props.theme.fontSize.xs};
    width: 302px;
    height: 370px;
    padding: 2.5rem;
    margin: 1rem;
    text-align: start;
    border-radius: 20px;
    align-items: start;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease-in-out;
    position: relative;
    overflow: hidden;

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
        transition: 0.5s;
    }

    &:hover {
        transform: translateY(-5px);
        border: 1px solid rgba(111, 120, 245, 0.3);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        background: linear-gradient(145deg, rgba(40, 54, 72, 0.95) 0%, rgba(40, 54, 72, 0.85) 100%);

        &:before {
            left: 100%;
        }

        img {
            transform: scale(1.1);
        }
    }

    > div,
    p {
        padding: 0.75rem 0;
    }

    img {
        transition: transform 0.3s ease;
    }

    .first-text,
    .second-text {
        font-size: ${props => props.theme.fontSize.xs};
        color: #bdc1c5;
        margin-top: 0.5rem;
        line-height: 1.6;
    }

    .second-text {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6f78f5;
        margin-top: 1rem;
        font-weight: 500;

        &:hover {
            color: #8891ff;
        }
    }

    h3 {
        font-size: ${props => props.theme.fontSize.sm};
        line-height: 1.5rem;
        margin-top: 1.5rem;
        background: linear-gradient(90deg, #ffffff 0%, #b7b7b7 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    @media (max-width: ${props => props.theme.bps.md}) {
        height: auto;
        width: 80vw;
        padding: 1.5rem;

        // Create a header group for icon and title
        .feature-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        h3 {
            margin-top: 0 !important; // Override the existing margin
            font-size: ${props => props.theme.fontSize.sm} !important;
        }
    }

    @media (min-width: ${props => props.theme.bps.md}) {
        width: 27vw;
    }

    @media (min-width: ${props => props.theme.bps.xl}) {
        width: 30rem;
        padding: 2.5rem;

        .first-text,
        .second-text {
            font-size: ${props => props.theme.fontSize.sm};
        }

        h3 {
            font-size: ${props => props.theme.fontSize.md};
            line-height: 2rem;
        }

        img {
            width: 50px;
        }
    }
`

const ButtonContainer = styled.div`
    display: flex;
    align-items: stretch;
    gap: 0.5rem;

    button {
        padding: 0;
        border-radius: 12px;
        font-weight: 500;
        transition: all 0.3s ease;
        background: linear-gradient(135deg, #6f78f5 0%, #4e57d5 100%);
        border: none;
        color: white;
        min-height: 54px;

        > div {
            height: 100%;
            padding: 0 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(111, 120, 245, 0.3);
        }

        span {
            margin: 0;
            font-size: 1rem;
        }
    }

    @media (max-width: ${props => props.theme.bps.md}) {
        flex-direction: column;
        width: 100%;

        button {
            width: 100%;
            margin-bottom: 1rem;
        }
    }
`

const GradientImage = styled.img`
    position: absolute;
    z-index: 0;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;

    transform: translate(-50%, -60%);

    @media (min-width: ${props => props.theme.bps.md}) {
        width: 80%;
        height: 80%;
    }
`

const GradientTransition = styled.div`
    height: 200px;
    width: 100%;
    position: absolute;
    top: 0px;
    background: linear-gradient(to bottom, rgba(40, 54, 72, 1) 0%, rgba(40, 54, 72, 0) 100%);
    z-index: 10;

    @media (max-width: ${props => props.theme.bps.md}) {
        height: 100px;
    }
`

const HeroSection: FC<{
    availableCurrencies: ICurrencyDto[]
    availablePairs: IValidatedPairsDto
    prices: TPriceWindowApiDto[]
}> = ({ availableCurrencies, availablePairs, prices }) => {
    const { t } = useTranslation('home')
    const ref = useRef<HTMLDivElement>(null)
    const [email, setEmail] = useState<string>('')
    const handleClick = () => {
        ref.current?.scrollIntoView({ behavior: 'smooth' })
    }
    const [exchangeWidgetState, dispatchExchangeWidgetAction] = useReducer(
        DataParamsReducer,
        dataParamsInitState || { tickers: [] },
    )
    useEffect(() => {
        dispatchExchangeWidgetAction(new SetCoinDataAction({ tickers: prices }))
    }, [prices])

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }
    return (
        <HeroContainer id="hero-container">
            <Section>
                <HeroHeadingSubSection>
                    <LayoutFirstContainer>
                        <InfoFirstContainer>
                            <h1>
                                {t('hero.title1')} <span className="highlight">{t('hero.titleHighlight')}</span> <br /> {t('hero.title2')}
                            </h1>
                            <h2>
                                {t('hero.subtitle1')} <br /> {t('hero.subtitle2')}
                            </h2>
                            <div className="buttons">
                                <a
                                    href="https://apps.apple.com/app/easywallet-crypto-simplified/id6479515101"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        className="btn"
                                        src="/images/easywallet/apple.svg"
                                        alt="Download on App Store"
                                    />
                                </a>
                                <a
                                    href="https://play.google.com/store/apps/details?id=com.yoa.easywallet&hl=en"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        className="btn"
                                        src="/images/easywallet/google.svg"
                                        alt="Get it on Google Play"
                                    />
                                </a>
                            </div>
                        </InfoFirstContainer>
                        <GradientTransition />

                        <Image
                            className="first-landing-img"
                            src="/images/easywallet/iphone14.webp"
                            alt="hero-phone"
                            loading="eager"
                            width={300}
                            height={600}
                            priority
                        />
                    </LayoutFirstContainer>

{/* ExchangeWidget section commented out - landing only
                    <Section
                        ref={ref}
                        style={{ marginTop: '3rem', position: 'relative', minHeight: '800px' }}
                    >
                        <div
                            style={{
                                position: 'relative',
                                zIndex: 1,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <ExchangeWidget
                                availableCurrencies={availableCurrencies}
                                availablePairs={availablePairs}
                                coinTypeAvailabilityModeFrom={coinTypeAvailabilityModeFrom}
                                coinTypeAvailabilityModeTo={coinTypeAvailabilityModeTo}
                                isCoinSwitchEnabled
                                isFullscreenSizeEnabled={false}
                            />
                        </div>
                    </Section>
                    */}

                    <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                        <h3 style={{ margin: '25px', fontSize: '40px', lineHeight: '48px' }}>
                            {t('features.title')}
                        </h3>
                        <Features>
                            <FeatureContainer>
                                <div className="feature-header">
                                    <FeatureIconWrapper>
                                        <Image
                                            src="/images/easywallet/3.png"
                                            alt="hero-buy-card"
                                            loading="eager"
                                            width={24}
                                            height={24}
                                            style={{ width: '100%', height: '100%' }}
                                        />
                                    </FeatureIconWrapper>
                                    <h3>
                                        {t('features.buyCrypto.title1')} <br /> {t('features.buyCrypto.title2')}
                                    </h3>
                                </div>
                                <Paragraph>
                                    {t('features.buyCrypto.description')}
                                </Paragraph>
                            </FeatureContainer>

                            <FeatureContainer>
                                <div className="feature-header">
                                    <FeatureIconWrapper>
                                        <Image
                                            src="/images/easywallet/2.png"
                                            alt="hero-security"
                                            loading="lazy"
                                            width={24}
                                            height={24}
                                            style={{ width: '100%', height: '100%' }}
                                        />
                                    </FeatureIconWrapper>
                                    <h3>
                                        {t('features.security.title1')} <br /> {t('features.security.title2')}
                                    </h3>
                                </div>
                                <Paragraph>
                                    {t('features.security.description')}
                                </Paragraph>
                            </FeatureContainer>

                            <FeatureContainer>
                                <div className="feature-header">
                                    <FeatureIconWrapper>
                                        <Image
                                            src="/images/easywallet/1.png"
                                            alt="hero-swap"
                                            loading="lazy"
                                            width={24}
                                            height={24}
                                            style={{ width: '100%', height: '100%' }}
                                        />
                                    </FeatureIconWrapper>
                                    <h3>
                                        {t('features.swap.title1')} <br /> {t('features.swap.title2')}
                                    </h3>
                                </div>
                                <Paragraph>{t('features.swap.description')}</Paragraph>
                            </FeatureContainer>
                        </Features>
                    </div>

                    <LayoutContainer>
                        <Image
                            className="landing-img-swap"
                            src="/images/easywallet/Group 7095.svg"
                            alt="hero-buy-card"
                            width={600}
                            height={500}
                            loading="eager"
                        />
                        <InfoContainer className="info-container">
                            <h3>{t('sections.buyCrypto.title')}</h3>
                            <Paragraph>
                                {t('sections.buyCrypto.description')}
                            </Paragraph>
                            <ButtonContainer style={{ marginTop: '1rem', display: 'flex' }}>
                                <Button
                                    onClick={sendEmail}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Filled}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>{t('buttons.getWallet')}</span>
                                        <FontAwesomeIcon
                                            icon={faArrowRight as IconProp}
                                            width={20}
                                        />
                                    </div>
                                </Button>
                                <Button
                                    onClick={handleClick}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Plain}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>{t('buttons.tryNow')}</span>
                                        <Image
                                            width={20}
                                            height={20}
                                            src="/images/easywallet/card.svg"
                                            alt="Card icon"
                                            loading="lazy"
                                        />
                                    </div>
                                </Button>
                            </ButtonContainer>
                        </InfoContainer>
                    </LayoutContainer>

                    <LayoutContainerImageRight>
                        <Image
                            className="landing-img-swap"
                            src="/images/easywallet/withdraw.svg"
                            alt="withdraw crypto to fiat"
                            width={600}
                            height={500}
                        />
                        <InfoContainer className="info-container">
                            <h3>
                                {t('sections.withdrawFiat.title1')} <br /> {t('sections.withdrawFiat.title2')}
                            </h3>
                            <Paragraph>
                                {t('sections.withdrawFiat.description')}
                            </Paragraph>
                            <ButtonContainer style={{ marginTop: '1rem', display: 'flex' }}>
                                <Button
                                    onClick={sendEmail}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Filled}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>{t('buttons.getWallet')}</span>
                                        <FontAwesomeIcon
                                            icon={faArrowRight as IconProp}
                                            width={20}
                                        />
                                    </div>
                                </Button>
                                <Button
                                    onClick={handleClick}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Plain}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>{t('buttons.tryNow')}</span>
                                        <Image
                                            width={20}
                                            height={20}
                                            src="/images/easywallet/card.svg"
                                            alt="Card icon"
                                            loading="lazy"
                                        />
                                    </div>
                                </Button>
                            </ButtonContainer>
                        </InfoContainer>
                    </LayoutContainerImageRight>

                    <LayoutContainer>
                        <Image
                            className="landing-img-swap"
                            src="/images/easywallet/swap.svg"
                            alt="hero-swap"
                            width={600}
                            height={500}
                        />
                        <InfoContainer className="info-container">
                            <h3>{t('sections.swapCrypto.title')}</h3>
                            <br />
                            <Paragraph>
                                {t('sections.swapCrypto.description')}
                            </Paragraph>
                            <ButtonContainer style={{ marginTop: '1rem', display: 'flex' }}>
                                <Button
                                    onClick={sendEmail}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Filled}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>{t('buttons.getWallet')}</span>
                                        <FontAwesomeIcon
                                            icon={faArrowRight as IconProp}
                                            width={20}
                                        />
                                    </div>
                                </Button>
                                <Button
                                    onClick={handleClick}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Plain}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>{t('buttons.tryNow')}</span>
                                        <Image
                                            width={20}
                                            height={20}
                                            src="/images/easywallet/card.svg"
                                            alt="Card icon"
                                            loading="lazy"
                                        />
                                    </div>
                                </Button>
                            </ButtonContainer>
                        </InfoContainer>
                    </LayoutContainer>

                    <LayoutTwoContainer>
                        <p style={{ marginBottom: '2rem', color: '#bec2c7' }}>
                            {t('testimonial.text1')} <br /> {t('testimonial.text2')} <br /> {t('testimonial.text3')}
                        </p>
                        <Image
                            src="/images/easywallet/investopedia.svg"
                            alt="hero-investopedia"
                            loading="eager"
                            width={200}
                            height={40}
                        />
                    </LayoutTwoContainer>

                    <LayoutGetUsdtContainer>
                        <InfoUsdtContainer>
                            <h3>
                                {t('cta.title1')} <br />
                                {t('cta.title2')}
                            </h3>
                            <span style={{ fontWeight: 500, fontSize: '20px', color: '#bec2c7' }}>
                                {t('cta.subtitle')}
                            </span>
                            <ButtonContainer style={{ display: 'flex' }}>
                                <StyledInput
                                    type="text"
                                    placeholder={t('cta.emailPlaceholder')}
                                    onChange={handleEmailChange}
                                />
                                <Button
                                    onClick={event => sendEmail(event, email)}
                                    type="button"
                                    styleVariant={EButtonStyleVariantClass.Filled}
                                >
                                    <div>
                                        <span style={{ marginRight: '10px' }}>
                                            {t('cta.button')}
                                        </span>
                                        <FontAwesomeIcon
                                            icon={faArrowRight as IconProp}
                                            width={20}
                                        />
                                    </div>
                                </Button>
                            </ButtonContainer>
                        </InfoUsdtContainer>
                        <Image
                            src="/images/easywallet/iphone14 (1).png"
                            alt="hero-iphone"
                            width={300}
                            height={400}
                            style={{ position: 'relative', zIndex: 2 }}
                        />
                    </LayoutGetUsdtContainer>
                </HeroHeadingSubSection>
            </Section>
        </HeroContainer>
    )
}

export default HeroSection
